<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from 'vue';
import { useRoute, useRouter, type RouteParamsRaw } from 'vue-router';
import { useElementSize, useScroll } from '@vueuse/core';
import { canGotoStep } from '@/helpers/loanErhlCustomerAppMachine';
import { loadSavedState, useErhlCustomerLoanAppProcess } from '@/helpers/useErhlCustomerLoanAppProcess';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import ErhlProgressNav from '@/views/erhl-customer-application/modules/ErhlProgressNav.vue';
import {
  ErhlCustomerApplicationProcessSteps,
  ErhlCustomerProcessStep,
  type ErhlCustomerProcessStepEnum,
} from '@/types';

/** `finishType` route param for the completed page; the customer ERHL journey has only one */
const ERHL_CUSTOMER_FINISH_TYPE = 'erhl';

const { send, service, state, stepper, currentStep } = useErhlCustomerLoanAppProcess();
const { y, directions } = useScroll(window);

const hideProgressNav = ref(false);
watch(y, (currY, oldY) => {
  const delta = Math.abs(currY - oldY);
  if (directions.bottom && currY > 100) {
    hideProgressNav.value = true;
  }
  if (directions.top && (currY <= 100 || delta > 10)) {
    hideProgressNav.value = false;
  }
});

provide('navInfo', { mainScrollOffset: y });

const navRef = ref(null);
const { height } = useElementSize(navRef);
const navheight = computed(() => `${height.value - y.value}px`);

const appVersion = computed(() => import.meta.env.VITE_APP_VERSION);

const route = useRoute();
const router = useRouter();

/** Route of the welcome page every new application starts on */
const WELCOME_ROUTE = 'erhlCustomerReady';

const showNav = computed(() => route.name !== ErhlCustomerProcessStep.Finished && route.name !== WELCOME_ROUTE);

const gotoStep = (step: ErhlCustomerProcessStepEnum) => {
  const stepRoute = ErhlCustomerApplicationProcessSteps[step]?.route;
  if (stepRoute && stepRoute !== route.name) {
    router.push({ name: stepRoute });
  }
};

watch(
  () => route.name,
  (newRoute, oldRoute) => {
    if (newRoute === oldRoute) return;

    // Once the application is submitted, prevent browser back navigation from
    // re-entering any step: always redirect forward to the confirmation page.
    if (
      state.value.context.current === ErhlCustomerProcessStep.Finished &&
      newRoute !== ErhlCustomerProcessStep.Finished
    ) {
      router.replace({ name: ErhlCustomerProcessStep.Finished, params: { finishType: ERHL_CUSTOMER_FINISH_TYPE } });
      return;
    }

    const step = stepper.value.find((s) => s.route === newRoute)?.id;
    if (!step) return;

    // A direct route to a step that isn't reachable yet would be rejected by the machine's GOTO
    // guard, leaving the router on an incomplete step; send the user back to their current step.
    const { status, current } = state.value.context;
    if (!canGotoStep(status, step)) {
      const currentRoute = ErhlCustomerApplicationProcessSteps[current]?.route;
      if (currentRoute && currentRoute !== newRoute) {
        router.replace({ name: currentRoute });
      }
      return;
    }

    send({ type: 'GOTO', step });
  },
  { immediate: true },
);

/**
 * Restores a saved customer ERHL journey and routes to its current step. A new visitor has no saved
 * journey, so they stay on the welcome page until they choose to start the application.
 */
const bootstrapErhlCustomer = () => {
  const newState = loadSavedState();
  if (!newState) {
    if (route.name !== WELCOME_ROUTE) {
      router.replace({ name: WELCOME_ROUTE });
    }
    return;
  }

  const savedStep = newState.current as ErhlCustomerProcessStepEnum | undefined;
  const savedRoute = savedStep ? ErhlCustomerApplicationProcessSteps[savedStep]?.route : null;
  const targetRoute = savedRoute ?? WELCOME_ROUTE;

  const targetParams: RouteParamsRaw | undefined =
    targetRoute === ErhlCustomerProcessStep.Finished ? { finishType: ERHL_CUSTOMER_FINISH_TYPE } : undefined;

  if (route.name !== targetRoute) {
    router.push({ name: targetRoute, params: targetParams });
  }

  send({ type: 'RELOAD', state: newState });
};

onMounted(bootstrapErhlCustomer);

service.onTransition((transition) => {
  if (transition.value !== 'gotoReady' && transition.value !== 'start') {
    const key = transition.value as ErhlCustomerProcessStepEnum;
    const name = ErhlCustomerApplicationProcessSteps[key]?.route;
    if (!name) return;

    if (route.name !== name) {
      if (name === ErhlCustomerProcessStep.Finished) {
        router.replace({ name, params: { finishType: ERHL_CUSTOMER_FINISH_TYPE } });
      } else {
        router.push({ name });
      }
    }
  }
});
</script>

<template>
  <div class="app-layout" :class="{ 'app-layout--hide-nav': !showNav }">
    <header class="app-layout__header">
      <AppHeader />
    </header>
    <nav ref="navRef" class="app-layout__nav" :class="{ hide: hideProgressNav }">
      <div class="nav-container">
        <ErhlProgressNav
          v-if="showNav"
          :model-value="currentStep"
          :end-debt="state.context.loanApp.endDebt"
          :steps="stepper"
          class="my-6"
          @update:model-value="gotoStep"
        />
      </div>
    </nav>
    <main class="app-layout__main">
      <RouterView v-slot="{ Component }">
        <Transition name="fade">
          <div v-if="Component" :key="route.fullPath" style="width: 100%">
            <component :is="Component" />
          </div>
        </Transition>
      </RouterView>
    </main>
    <footer class="app-layout__footer">
      <AppFooter />
      <div class="app-version">v{{ appVersion }}</div>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
.app-layout {
  display: grid;
  grid-template:
    'header' minmax(64px, auto) 'nav   ' 100px
    'main  ' 1fr
    'footer' auto / 1fr;

  width: 100vw;
  min-height: 100vh;

  // `clip` (unlike `hidden`) doesn't trigger the CSS overflow-x/overflow-y interop rule that
  // silently computes the other axis to `auto` — that rule was turning this element into its
  // own scroll container, which stole the sticky EquitySnapshot's "nearest scrolling ancestor"
  // away from the real window scroll, breaking its pinned-to-top behaviour.
  overflow-x: clip;

  &__header {
    @apply z-30 border-b border-gray-100 bg-white;
    grid-area: header;

    position: fixed;
    top: 0;
    left: 0;
    right: 0;

    height: 68px;
  }

  &__nav {
    @apply bg-bridgit-navy;
    @apply pl-0 md:pl-4 lg:pl-8;
    @apply z-20 flex justify-end;
    grid-area: nav;

    transition: top 0.3s ease-in-out;
  }

  &__main {
    @apply bg-bridgit-paleBlue;
    @apply relative flex px-4 py-6 md:px-6 lg:px-8;
    grid-area: main;
  }

  &__footer {
    @apply relative z-30 bg-white;
    grid-area: footer;
  }

  &.app-layout--hide-nav {
    grid-template:
      'header' minmax(64px, auto) 'main  ' 1fr
      'footer' auto / 1fr;

    .app-layout__nav {
      display: none;
    }

    .app-layout__main {
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    &__nav {
      position: fixed;
      top: 68px;
      left: 0;
      right: 0;
      z-index: 100;

      height: 100px;

      &.hide {
        top: -4rem;

        transition: top 0.3s ease-in-out;
      }
    }
  }

  @screen md {
    grid-template:
      'header header' minmax(64px, auto) 'nav main     ' 1fr
      'footer footer' minmax(140px, auto) / 2fr 5fr;
  }
}

:deep(.section-panel) {
  background-color: #fff;

  @media (max-width: 767px) {
    padding: 1rem;
  }

  @media (min-width: 768px) {
    --border-radius: 0.5rem;
    --border-color: #ccc;

    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
  }
}

.nav-container {
  @apply w-full overflow-auto md:fixed md:top-16 md:w-auto;

  @screen md {
    height: v-bind(navheight);
  }
}

.app-version {
  @apply text-gray-200;
  position: absolute;
  bottom: 0;
  right: 0;

  font-size: 8px;
}
</style>
